import React, { createContext, useContext, useEffect, useState } from 'react';
import { preferenciasService } from '../services/api/preferenciasService';
import { PreferenciasUsuario } from '../types/preferencias';
import { authService } from '../services/authService';

interface PreferenciasContextType {
  preferencias: PreferenciasUsuario | null;
  carregando: boolean;
  erro: string | null;
  salvarPreferencia: (campo: keyof PreferenciasUsuario, valor: any) => Promise<boolean>;
  atualizarMultiplasPreferencias: (dados: Partial<PreferenciasUsuario>) => Promise<boolean>;
  recarregarPreferencias: () => Promise<void>;
  aplicarTema: () => void;
}

const PreferenciasContext = createContext<PreferenciasContextType | undefined>(undefined);

interface PreferenciasProviderProps {
  children: React.ReactNode;
}

export function PreferenciasProvider({ children }: PreferenciasProviderProps) {
  const [preferencias, setPreferencias] = useState<PreferenciasUsuario | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  /**
   * Carregar preferências do servidor
   */
  const carregarPreferencias = async () => {
    try {
      setCarregando(true);
      setErro(null);

      // Verificar autenticação
      if (!authService.isAuthenticated()) {
//         console.log('[PreferenciasProvider] Usuário não autenticado - aplicando tema padrão');
        preferenciasService.aplicarPreferenciasPadrao();
        return;
      }

//       console.log('[PreferenciasProvider] Carregando preferências do servidor:', {
//         timestamp: new Date().toISOString(),
//         usuario: authService.getUsuario()?.id
//       });

      const resultado = await preferenciasService.buscarPreferencias();

      if (resultado.sucesso && resultado.dados) {
        setPreferencias(resultado.dados);
        
        // Aplicar tema automaticamente
        preferenciasService.aplicarTemaCompleto(resultado.dados);
        
        // Salvar no localStorage para próxima inicialização
        localStorage.setItem('preferencias_usuario', JSON.stringify(resultado.dados));
        localStorage.setItem('tema_carregado', 'true');
        
//         console.log('[PreferenciasProvider] Preferências carregadas e aplicadas:', {
//           userId: authService.getUsuario()?.id,
//           darkMode: resultado.dados.dark_mode,
//           temaCor: resultado.dados.tema_cor,
//           layoutFixo: resultado.dados.layout_fixo,
//           timestamp: new Date().toISOString()
//         });
      } else {
//         console.warn('[PreferenciasProvider] Preferências não encontradas, aplicando padrão:', resultado.mensagem);
        setErro(resultado.mensagem);
        preferenciasService.aplicarPreferenciasPadrao();
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      console.error('[PreferenciasProvider] Erro ao carregar preferências:', {
        error: errorMessage,
        timestamp: new Date().toISOString(),
        usuario: authService.getUsuario()?.id
      });
      
      setErro(errorMessage);
      preferenciasService.aplicarPreferenciasPadrao();
    } finally {
      setCarregando(false);
    }
  };

  /**
   * Salvar uma preferência específica
   */
  const salvarPreferencia = async (campo: keyof PreferenciasUsuario, valor: any): Promise<boolean> => {
    try {
      if (!authService.isAuthenticated()) {
        console.error('[PreferenciasProvider] Usuário não autenticado para salvar preferência');
        return false;
      }

//       console.log('[PreferenciasProvider] Salvando preferência:', {
//         campo,
//         valor,
//         timestamp: new Date().toISOString(),
//         usuario: authService.getUsuario()?.id
//       });

      const dadosAtualizacao = { [campo]: valor };
      const resultado = await preferenciasService.atualizarPreferencias(dadosAtualizacao);

      if (resultado.sucesso && resultado.dados) {
        setPreferencias(resultado.dados);
        
        // Aplicar tema automaticamente
        preferenciasService.aplicarTemaCompleto(resultado.dados);
        
        // Atualizar localStorage
        localStorage.setItem('preferencias_usuario', JSON.stringify(resultado.dados));
        
//         console.log('[PreferenciasProvider] Preferência salva e tema aplicado:', {
//           campo,
//           valor,
//           timestamp: new Date().toISOString()
//         });

        return true;
      } else {
        console.error('[PreferenciasProvider] Erro ao salvar preferência:', resultado.mensagem);
        setErro(resultado.mensagem);
        return false;
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      console.error('[PreferenciasProvider] Erro ao salvar preferência:', {
        error: errorMessage,
        campo,
        valor,
        timestamp: new Date().toISOString()
      });
      
      setErro(errorMessage);
      return false;
    }
  };

  /**
   * Atualizar múltiplas preferências de uma vez
   */
  const atualizarMultiplasPreferencias = async (dados: Partial<PreferenciasUsuario>): Promise<boolean> => {
    try {
      if (!authService.isAuthenticated()) {
        console.error('[PreferenciasProvider] Usuário não autenticado para atualizar preferências');
        return false;
      }

//       console.log('[PreferenciasProvider] Atualizando múltiplas preferências:', {
//         campos: Object.keys(dados),
//         timestamp: new Date().toISOString(),
//         usuario: authService.getUsuario()?.id
//       });

      const resultado = await preferenciasService.atualizarPreferencias(dados);

      if (resultado.sucesso && resultado.dados) {
        setPreferencias(resultado.dados);
        
        // Aplicar tema automaticamente
        preferenciasService.aplicarTemaCompleto(resultado.dados);
        
        // Atualizar localStorage
        localStorage.setItem('preferencias_usuario', JSON.stringify(resultado.dados));
        
//         console.log('[PreferenciasProvider] Múltiplas preferências salvas e tema aplicado:', {
//           campos: Object.keys(dados),
//           timestamp: new Date().toISOString()
//         });

        return true;
      } else {
        console.error('[PreferenciasProvider] Erro ao atualizar preferências:', resultado.mensagem);
        setErro(resultado.mensagem);
        return false;
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      console.error('[PreferenciasProvider] Erro ao atualizar múltiplas preferências:', {
        error: errorMessage,
        campos: Object.keys(dados),
        timestamp: new Date().toISOString()
      });
      
      setErro(errorMessage);
      return false;
    }
  };

  /**
   * Recarregar preferências do servidor
   */
  const recarregarPreferencias = async () => {
    await carregarPreferencias();
  };

  /**
   * Aplicar tema com as preferências atuais
   */
  const aplicarTema = () => {
    if (preferencias) {
      preferenciasService.aplicarTemaCompleto(preferencias);
    } else {
      // Tentar aplicar do localStorage
      const preferenciasLocal = preferenciasService.obterPreferenciasDoLocalStorage();
      if (preferenciasLocal) {
        preferenciasService.aplicarTemaCompleto(preferenciasLocal);
      } else {
        preferenciasService.aplicarPreferenciasPadrao();
      }
    }
  };

  /**
   * Inicialização do provider
   */
  useEffect(() => {
    const inicializar = async () => {
//       console.log('[PreferenciasProvider] Inicializando provider de preferências:', {
//         timestamp: new Date().toISOString()
//       });

      // 1. Aplicar tema do localStorage imediatamente (carregamento rápido)
      const aplicouDoLocalStorage = preferenciasService.aplicarPreferenciasDoLocalStorage();
      
      if (aplicouDoLocalStorage) {
//         console.log('[PreferenciasProvider] Tema aplicado do localStorage para carregamento rápido');
      }

      // 2. Aguardar inicialização do authService
      await authService.waitForInitialization();

      // 3. Se usuário está autenticado, carregar preferências atualizadas do servidor
      if (authService.isAuthenticated()) {
//         console.log('[PreferenciasProvider] Usuário autenticado - carregando preferências do servidor');
        await carregarPreferencias();
      } else if (!aplicouDoLocalStorage) {
        // 4. Se não tem localStorage e não está autenticado, aplicar padrão
//         console.log('[PreferenciasProvider] Aplicando tema padrão');
        preferenciasService.aplicarPreferenciasPadrao();
      }
    };

    inicializar();
  }, []);

  /**
   * Escutar mudanças de autenticação e aplicar preferências
   */
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token') {
        if (e.newValue) {
          // Token foi adicionado - usuário fez login
//           console.log('[PreferenciasProvider] Login detectado - carregando preferências imediatamente');
          
          // Aguardar um pouco para garantir que o authService seja atualizado
          setTimeout(async () => {
            await authService.waitForInitialization();
            if (authService.isAuthenticated()) {
              await carregarPreferencias();
            }
          }, 100);
        } else {
          // Token foi removido - usuário fez logout
//           console.log('[PreferenciasProvider] Logout detectado - limpando preferências');
          setPreferencias(null);
          localStorage.removeItem('preferencias_usuario');
          localStorage.removeItem('tema_carregado');
          preferenciasService.aplicarPreferenciasPadrao();
        }
      }
    };

    // Escutar eventos de localStorage (cross-tab)
    window.addEventListener('storage', handleStorageChange);

    // Escutar eventos personalizados de autenticação (mesma aba)
    const handleAuthChange = (event: CustomEvent) => {
//       console.log('[PreferenciasProvider] Evento de autenticação detectado:', event.detail);
      
      if (event.detail.type === 'login') {
        setTimeout(async () => {
          await authService.waitForInitialization();
          if (authService.isAuthenticated()) {
//             console.log('[PreferenciasProvider] Carregando preferências após login local');
            await carregarPreferencias();
          }
        }, 100);
      } else if (event.detail.type === 'logout') {
//         console.log('[PreferenciasProvider] Limpando preferências após logout local');
        setPreferencias(null);
        localStorage.removeItem('preferencias_usuario');
        localStorage.removeItem('tema_carregado');
        preferenciasService.aplicarPreferenciasPadrao();
      }
    };

    window.addEventListener('authChange', handleAuthChange as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authChange', handleAuthChange as EventListener);
    };
  }, []);

  /**
   * Monitorar mudanças na autenticação e garantir que o tema seja aplicado
   */
  useEffect(() => {
    const interval = setInterval(() => {
      // Se usuário está autenticado mas ainda não carregamos as preferências do servidor
      if (authService.isAuthenticated() && !preferencias && !carregando) {
//         console.log('[PreferenciasProvider] Usuário autenticado sem preferências - tentando carregar');
        carregarPreferencias();
      }
      
      // Se não há preferências e não está autenticado, garantir tema padrão
      if (!authService.isAuthenticated() && !preferencias) {
        const temLocalStorage = preferenciasService.obterPreferenciasDoLocalStorage();
        if (!temLocalStorage) {
          preferenciasService.aplicarPreferenciasPadrao();
        }
      }
    }, 2000); // Verificar a cada 2 segundos

    return () => clearInterval(interval);
  }, [preferencias, carregando]);

  const contextValue: PreferenciasContextType = {
    preferencias,
    carregando,
    erro,
    salvarPreferencia,
    atualizarMultiplasPreferencias,
    recarregarPreferencias,
    aplicarTema
  };

  return (
    <PreferenciasContext.Provider value={contextValue}>
      {children}
    </PreferenciasContext.Provider>
  );
}

/**
 * Hook para usar o contexto de preferências
 */
export function usePreferenciasContext(): PreferenciasContextType {
  const context = useContext(PreferenciasContext);
  if (context === undefined) {
    throw new Error('usePreferenciasContext deve ser usado dentro de PreferenciasProvider');
  }
  return context;
}

/**
 * Hook para dark mode específico usando o contexto
 */
export function useDarkMode() {
  const { preferencias, salvarPreferencia } = usePreferenciasContext();
  
  const darkMode = preferencias?.dark_mode || false;
  
  const toggleDarkMode = async () => {
    await salvarPreferencia('dark_mode', !darkMode);
  };

  const setDarkMode = async (ativo: boolean) => {
    await salvarPreferencia('dark_mode', ativo);
  };

  return {
    darkMode,
    toggleDarkMode,
    setDarkMode
  };
}
